import { isCarsTrucks } from '@kijiji/category'
import {
  AdSource,
  EventsUrlType,
  useEventsExternalClickMutation,
  useVehicleReportQuery,
} from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import qs from 'query-string'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { type ImageData } from '@/components/shared/image/ImageCustom'
import { VipSectionTitle } from '@/components/vip/shared/vip-section-title/VipSectionTitle'
import { VipSectionContainer } from '@/components/vip/shared/VipSectionContainer'
import { CarfaxLogoImage } from '@/components/vip/vip-vehicle-history/styled'
import { VehicleHistoryLearnMoreModal } from '@/components/vip/vip-vehicle-history/VehicleHistoryLearnMoreModal'
import { VehicleHistoryRequestBody } from '@/components/vip/vip-vehicle-history/VehicleHistoryRequestBody'
import { VehicleHistoryRequestModal } from '@/components/vip/vip-vehicle-history/VehicleHistoryRequestModal'
import { VehicleHistoryViewBody } from '@/components/vip/vip-vehicle-history/VehicleHistoryViewBody'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type VipQueryListing } from '@/types/listings'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { TextLink } from '@/ui/atoms/text-link'

const CARFAX_LOGO: ImageData = {
  src: '/next-assets/images/carfax_logo.svg',
}

type VehicleHistoryType = 'view' | 'request' | 'buy'

export type VipVehicleHistoryProps = {
  listing: VipQueryListing
}

export const VipVehicleHistory: FC<VipVehicleHistoryProps> = ({ listing }) => {
  const { handleOpenModal: openLearnMoreModal } = useModal(MODAL_REGISTRY.VEHICLE_HISTORY_LEARN)
  const { handleOpenModal: openRequestModal } = useModal(MODAL_REGISTRY.VEHICLE_HISTORY_REQUEST)

  const { VEHICLE_TYPE, VIN } = ATTRIBUTES
  const [eventsExternalClick] = useEventsExternalClickMutation()

  const { t } = useTranslation(TRANSLATION_KEYS.VIP)
  const { routeLocale } = useLocale()
  const { spacing } = useTheme()

  const { attributes, categoryId } = listing
  const isSm360 = !!listing.flags.isSM360

  const { data } = useVehicleReportQuery({
    skip: !isSm360,
    variables: { adId: parseInt(listing.id.replace('m', '')), source: AdSource.VerticalAutos },
  })

  const getCarfaxURLFromAttributes = () => {
    const carfaxFromAttributes = attributes?.all?.find(
      (item) => item?.canonicalName === ATTRIBUTES.CARFAX
    )

    const value = carfaxFromAttributes?.values?.[0]
    if (!value) return

    return value
  }

  const vehicleReport = data?.ad && 'vehicleReport' in data.ad ? data.ad.vehicleReport : undefined

  const isNewVehicle = () => {
    const vehicleType = attributes?.all?.find((item) => item?.canonicalName === VEHICLE_TYPE)
    return vehicleType?.canonicalValues?.[0]?.toLowerCase() === 'new'
  }

  /**
   * It should only show section if listing is on the Cars & Vehicles category
   * and the vehicle should not be "NEW"
   * */
  if (!isCarsTrucks(categoryId) || isNewVehicle()) return null

  const getCarfaxReportUrl = (carproofLink?: string | null) => {
    if (!carproofLink) return

    const parsedUrl = qs.parseUrl(carproofLink)
    const { query } = parsedUrl
    let { url } = parsedUrl

    /**
     * Old "reports.carproof.com" link redirect to vhr.carfax.ca
     * We should update the hostname while we get the URL
     */
    const oldHostName = 'reports.carproof.com'

    if (carproofLink.includes(oldHostName)) {
      const newHostName = 'vhr.carfax.ca'
      url = url.replace(oldHostName, newHostName)
    }

    /**
     * Verify if url comes with locale parameters and strip them to add it manually
     * The BE is always sending en parameters, we are fixing it here
     */
    url = new URL(url).origin
    return qs.stringifyUrl({ url: `${url}/${routeLocale}/`, query })
  }

  const getCarVin = () => {
    const vin = attributes?.all?.find((item) => item?.canonicalName === VIN)
    return vin?.values[0]
  }

  const getCarHistoryVariation = (
    reportUrlFromData?: string | null
  ): { type: VehicleHistoryType; externalUrl?: string } => {
    const reportUrl = reportUrlFromData ?? getCarfaxURLFromAttributes()

    if (isSm360 && reportUrl) {
      return { type: 'view', externalUrl: reportUrl }
    }

    if (reportUrl) {
      const carfaxReportUrl = getCarfaxReportUrl(reportUrl)
      return { type: 'view', externalUrl: carfaxReportUrl }
    }

    const vin = getCarVin()
    if (vin) {
      return { type: 'buy', externalUrl: t('vehicle_history.vin.url', { vin }) }
    }

    return { type: 'request' }
  }

  const handleLearnMoreClick = () => {
    openLearnMoreModal()
    trackEvent({ action: GA_EVENT.CarfaxLearnMore })
  }

  const handleCarfaxClick = (type: VehicleHistoryType, externalUrl: string | undefined) => {
    const isTypeBuy = type === 'buy'
    const label = isTypeBuy ? 'purchase' : type

    if (type === 'request') {
      openRequestModal()
    }

    trackEvent({ action: GA_EVENT.CarfaxRequest, label: `scenario=${label}` })

    if (isTypeBuy && externalUrl) {
      eventsExternalClick({
        variables: {
          eventsExternalClickId: listing.id,
          url: externalUrl,
          urlType: EventsUrlType.PartnerPage,
        },
      })
    }
  }

  const { type, externalUrl } = getCarHistoryVariation(vehicleReport?.reportUrl)

  const anchorProps = externalUrl ? { as: 'a', href: externalUrl, target: '_blank' } : {}

  return (
    <VipSectionContainer>
      <VipSectionTitle
        title={t('vip:vehicle_history.title')}
        logo={<CarfaxLogoImage srcImage={CARFAX_LOGO} skipNextImage alt="" />}
      />

      <Flex flexDirection="column" gap={spacing.default}>
        {type === 'request' || type === 'buy' ? (
          <VehicleHistoryRequestBody />
        ) : (
          <VehicleHistoryViewBody reportBadgeKeys={vehicleReport?.badgeKeys} />
        )}

        <Flex gap={spacing.default}>
          <Button
            variant="secondary"
            size="small"
            onClick={() => handleCarfaxClick(type, externalUrl)}
            {...anchorProps}
          >
            {t(`vip:vehicle_history.cta.${type}`)}
          </Button>

          {isSm360 ? (
            <TextLink
              as="button"
              weight="medium"
              hasUnderline
              onClick={handleLearnMoreClick}
              size="small"
              variant="secondary"
            >
              {t('vip:vehicle_history.learn_more')}
            </TextLink>
          ) : null}
        </Flex>
      </Flex>

      <VehicleHistoryLearnMoreModal carfaxLogoSrc={CARFAX_LOGO.src} />
      <VehicleHistoryRequestModal carfaxLogoSrc={CARFAX_LOGO.src} listingId={listing.id} />
    </VipSectionContainer>
  )
}
